/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  combineReducers,
  composeReducers,
  composeSetters,
  fieldValidationSetter,
  forceUpdatedDirtyType,
  removeDirtyRow,
  setDirty,
  setEntityField,
  setEntityFields,
  setNewDirtyType,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  Table,
  whenRemoved,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { constant, difference, flatMap } from 'lodash';
import {
  createHpeTop,
  getHpeComponentCostCenterIds,
  HpeTop,
  validateHpeTopField,
} from '../../../resources/hpe-details.model';
import {
  hpeCcTable,
  hpeComponentTable,
  HpeDetailsState,
  HpeDetailsStateEvent,
  HpeDetailsStateEventType,
  hpeItemTable,
  hpeTopTable,
  queryHpeTopRefItem,
} from '../hpe-details.state';

const setTopField = composeSetters(setEntityField, setUpdatedDirtyType, fieldValidationSetter(validateHpeTopField));

export const hpeDetailsTopReducer = composeReducers(
  combineReducers({
    hpTops: topsReducer,
  }),
  topDirtyFlow,
  processChangesAsapReducer,
  rootTopReducer,
);

function topsReducer(tops: Table<HpeTop>, event: HpeDetailsStateEvent): Table<HpeTop> {
  switch (event.type) {
    case HpeDetailsStateEventType.TopSetField:
      return hpeTopTable.mergeById(tops, event.topId, (row: HpeTop) => setTopField(row, event.name, event.value));
    case HpeDetailsStateEventType.TopCommit:
      // reset isFromGto flag if top was changed
      return hpeTopTable.mergeById(
        tops,
        event.topId,
        (top: HpeTop) => top.isFromGto ? setEntityField(top, 'isFromGto', false) : top);
    case HpeDetailsStateEventType.TopReset:
      return hpeTopTable.mergeById(tops, event.top.topId, constant(event.top));
    case HpeDetailsStateEventType.TopRemove:
      return hpeTopTable.mergeById(tops, event.topId, (top) => removeDirtyRow(setEntityField(top, 'isFromGto', false)));
    default:
      return tops;
  }
}

function topDirtyFlow(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.TopCommit: {
      const components = hpeComponentTable.byGroup(state.hpComponents, 'topId', event.topId);
      const items = hpeItemTable.byGroup(state.hpItems, 'topId', event.topId);
      const affectedCcIds = items
        .map((item) => item.costCenterId)
        .concat(components.length ? [state.hpComponentsCcId] : [])
        .concat(flatMap(components, getHpeComponentCostCenterIds));
      return setEntityFields(state, {
        hpComponents: hpeComponentTable.updateAll(state.hpComponents, components.map(setUpdatedDirtyType)),
        hpItems: hpeItemTable.updateAll(state.hpItems, items.map(setUpdatedDirtyType)),
        hpCostCenters: hpeCcTable.mergeAllById(state.hpCostCenters, affectedCcIds, setUpdatedDirtyType),
      });
    }
    default:
      return state;
  }
}

function processChangesAsapReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.TopCommit:
      return setEntityField(state, 'isProcessChangesAsap', true);
    default:
      return state;
  }
}

function rootTopReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.TopRemove:
      return setDirty(state);
    case HpeDetailsStateEventType.TopCommit:
      return setRecalculationNeeded(setDirty(state));
    default:
      return state;
  }
}

export function addOrUpdateTops(state: HpeDetailsState, topIds: ID[]): HpeDetailsState {
  return setEntityField(
    state,
    'hpTops',
    hpeTopTable.mergeAllById(
      state.hpTops,
      topIds,
      (row, id) => {
        if (row) {
          if (whenRemoved(row)) {
            return forceUpdatedDirtyType(row);
          } else {
            return setUpdatedDirtyType(row);
          }
        } else {
          const top = queryHpeTopRefItem(state, id);

          if (top) {
            return setNewDirtyType(createHpeTop(top));
          }
        }
      }));
}

export function getModifiedTops(sourceTopIds: ID[], targetTopIds: ID[]): ID[] {
  return difference(sourceTopIds, targetTopIds).concat(difference(targetTopIds, sourceTopIds));
}
